import { Injectable } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';
import { CognitoIdentityClient } from '@aws-sdk/client-cognito-identity';
import { AwsCredentialIdentity } from '@aws-sdk/types';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class CognitoCredentialsService {
  private credentialsCache: AwsCredentialIdentity | null = null;
  private credentialsExpiry: Date | null = null;
  private readonly CACHE_DURATION_MS = 50 * 60 * 1000; // 50 minutes (credentials expire after ~1 hour)

  constructor(private auth: Auth) {}

  /**
   * Extracts the region from an Identity Pool ID
   * Format: region:uuid (e.g., 'us-east-1:xxx-xxx-xxx' -> 'us-east-1')
   */
  private extractRegionFromIdentityPoolId(identityPoolId: string): string {
    const parts = identityPoolId.split(':');
    if (parts.length < 2) {
      throw new Error(`Invalid Identity Pool ID format: ${identityPoolId}`);
    }
    return parts[0];
  }

  /**
   * Gets the Firebase ID token, refreshing if necessary
   */
  private async getFirebaseIdToken(): Promise<string> {
    const user = this.auth.currentUser;
    if (!user) {
      console.error('[CognitoCredentials] No authenticated user found');
      throw new Error('User is not authenticated. Make sure Firebase authentication is successful.');
    }
    
    try {
      // Get ID token, which will be automatically refreshed if expired
      const idToken = await user.getIdToken();
      
      if (!idToken) {
        throw new Error('Failed to obtain ID token from Firebase');
      }
      
      return idToken;
    } catch (error: any) {
      console.error('[CognitoCredentials] Error getting Firebase ID token:', error);
      throw new Error(`Failed to get Firebase ID token: ${error.message}`);
    }
  }

  /**
   * Clears the credentials cache
   */
  public clearCredentialsCache(): void {
    this.credentialsCache = null;
    this.credentialsExpiry = null;
  }

  /**
   * Gets AWS credentials from Cognito Identity Pool using Firebase OIDC
   * Implements caching to avoid unnecessary token exchanges
   */
  public async getCredentials(retryCount: number = 0): Promise<AwsCredentialIdentity> {
    const maxRetries = 1;

    // Check if cached credentials are still valid
    if (this.credentialsCache && this.credentialsExpiry && new Date() < this.credentialsExpiry) {
      return this.credentialsCache;
    }

    try {
      // Get Firebase ID token
      const idToken = await this.getFirebaseIdToken();
      
      if (!idToken) {
        throw new Error('Failed to get Firebase ID token');
      }

      // Extract region from Identity Pool ID
      const identityPoolRegion = this.extractRegionFromIdentityPoolId(
        environment.cognito.identityPoolId
      );  

      // console.log('[CognitoCredentials] Getting credentials:', {
      //   identityPoolId: environment.cognito.identityPoolId,
      //   identityPoolRegion,
      //   oidcProvider: environment.cognito.oidcProvider,
      //   firebaseProjectId: environment.cognito.firebaseProjectId,
      //   hasIdToken: !!idToken,
      //   idTokenLength: idToken?.length
      // });

      // Create Cognito Identity client
      const cognitoIdentityClient = new CognitoIdentityClient({
        region: identityPoolRegion
      });

      // Get credentials from Cognito Identity Pool
      const credentialProvider = fromCognitoIdentityPool({
        client: cognitoIdentityClient,
        identityPoolId: environment.cognito.identityPoolId,
        logins: {
          [environment.cognito.oidcProvider]: idToken
        }
      });

      // Fetch credentials
      const credentials = await credentialProvider();

      // Cache credentials
      this.credentialsCache = credentials;
      this.credentialsExpiry = new Date(Date.now() + this.CACHE_DURATION_MS);

      // console.log('[CognitoCredentials] Successfully obtained credentials');
      return credentials;
    } catch (error: any) {
      // Clear cache on authentication errors
      this.clearCredentialsCache();

      // Enhanced error logging
      console.error('[CognitoCredentials] Error getting credentials:', error);

      // Handle specific error types
      if (error.name === 'NotAuthorizedException' && retryCount < maxRetries) {
        // Token expired or invalid - try refreshing
        try {
          const user = this.auth.currentUser;
          if (user) {
            // console.log('[CognitoCredentials] Attempting to refresh token...');
            // Force token refresh
            await user.getIdToken(true);
            // Retry getting credentials
            return this.getCredentials(retryCount + 1);
          } else {
            throw new Error('User is not authenticated');
          }
        } catch (refreshError: any) {
          //  console.error('[CognitoCredentials] Failed to refresh token:', refreshError);
          throw new Error(`Failed to refresh authentication token: ${refreshError.message}`);
        }
      }

      if (error.name === 'ResourceNotFoundException') {
        throw new Error(
          `Cognito Identity Pool not found. Check Identity Pool ID and region: ${environment.cognito.identityPoolId}. ` +
          `Make sure the Identity Pool exists in region ${this.extractRegionFromIdentityPoolId(environment.cognito.identityPoolId)} ` +
          `and the OIDC provider '${environment.cognito.oidcProvider}' is configured correctly.`
        );
      }

      if (error.name === 'InvalidIdentityTokenException' || error.message?.includes('token')) {
        throw new Error(
          `Invalid identity token. Make sure the OIDC provider '${environment.cognito.oidcProvider}' ` +
          `is correctly configured in AWS Cognito Identity Pool and matches Firebase project '${environment.cognito.firebaseProjectId}'. ` +
          `Original error: ${error.message}`
        );
      }

      // Re-throw with enhanced message
      throw new Error(
        `Failed to get Cognito credentials: ${error.message || error.name}. ` +
        `Identity Pool: ${environment.cognito.identityPoolId}, ` +
        `OIDC Provider: ${environment.cognito.oidcProvider}`
      );
    }
  }
}

